import { useCallback, useEffect } from "react";
import { useApolloClient, useMutation } from "@apollo/client";
import {
  FragmentType,
  getFragmentData,
  serverGql,
} from "__generated__/serverGql";
import serverClient from "utils/apollo/serverClient";
import { asyncVoid } from "utils/asyncVoidHandler";
import { gql } from "__generated__/gql";
import { assertPresence } from "utils/assertions";

const REFRESH_BRAND_CACHE_FIELDS = serverGql(/* ServerGraphQL */ `
  fragment RefreshBrandSubscriptionFields on Brand {
    id
    isSubscriptionValid
    stripeSubscriptionId
  }
`);

const MUTATION = serverGql(/* ServerGraphQL */ `
  mutation RefreshSubscriptionStatus($brandId: UUID!) {
    refreshSubscriptionStatus(brandId: $brandId) {
      status
      brand {
        id
        isSubscriptionValid
        stripeSubscriptionId
        ...RefreshBrandSubscriptionFields
      }
    }
  }
`);

const UPDATED_BRAND_FRAGMENT = gql(/* GraphQL */ `
  fragment UpdatedBrand on Brand {
    isSubscriptionValid
    stripeSubscriptionId
  }
`);

export function useUpdateBrandSubscriptionCache() {
  const client = useApolloClient();
  return useCallback(
    (brand: FragmentType<typeof REFRESH_BRAND_CACHE_FIELDS>) => {
      const brandData = getFragmentData(REFRESH_BRAND_CACHE_FIELDS, brand);
      const cacheId = client.cache.identify(brandData);
      assertPresence(cacheId, "Could not determine cache ID for brand");
      client.cache.writeFragment({
        id: cacheId,
        fragment: UPDATED_BRAND_FRAGMENT,
        data: {
          __typename: brandData.__typename,
          isSubscriptionValid: brandData.isSubscriptionValid,
          stripeSubscriptionId: brandData.stripeSubscriptionId,
        },
      });
    },
    [client],
  );
}

type UseRefreshSubscriptionStatusOptions = {
  brandId: string | null | undefined;
  stripeSubscriptionId: string | null | undefined;
  skip?: boolean;
};

export default function useRefreshSubscriptionStatus({
  brandId,
  stripeSubscriptionId,
  skip = false,
}: UseRefreshSubscriptionStatusOptions) {
  const updateBrandSubscriptionCache = useUpdateBrandSubscriptionCache();

  const [refreshStatus, mutationResult] = useMutation(MUTATION, {
    client: serverClient,

    fetchPolicy: "network-only",
  });

  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { data, reset } = mutationResult;

  // Auto-refresh subscription status on mount when subscription exists
  useEffect(() => {
    if (!brandId || !stripeSubscriptionId || skip) {
      reset();
      return;
    }

    asyncVoid(
      refreshStatus({ variables: { brandId } }).then((result) => {
        // Update the PostGraphile Apollo cache with the brand data from server mutation
        const updatedBrand = result.data?.refreshSubscriptionStatus?.brand;
        assertPresence(
          updatedBrand,
          "Brand data missing from mutation response",
        );
        updateBrandSubscriptionCache(updatedBrand);
      }),
    );
  }, [
    brandId,
    stripeSubscriptionId,
    refreshStatus,
    skip,
    reset,
    updateBrandSubscriptionCache,
  ]);

  return {
    subscriptionStatus: data?.refreshSubscriptionStatus?.status,
    refreshStatus,
    mutationResult,
  };
}
